import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../ui/badge';
import { GripVertical, Factory, Scissors, GitBranch } from 'lucide-react';
import StepSplitDialog from './StepSplitDialog';

function ProcessStepCard({
  step,
  productName,
  plannedQuantity,
  orderId,
  orderNumber,
  itemId,
  itemIndex,
  productCode,
  specification = '',
  completedQuantity = 0,
  isOverlay = false,
  isSubcontracted = false,
  onSplit,
}) {
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);

  // Use display quantity if this is a split step (_displayQty comes from sidebarOrders)
  const displayQty = step._displayQty ?? (plannedQuantity - completedQuantity);
  const isSplitOriginal = step._splitOriginal;
  const isSplitChild = step._splitChild;
  const splitId = step._splitId;

  // Unique draggable id for each split child
  const draggableId = isSplitChild
    ? `unassigned-step-${orderId}-${itemId}-${step.sequence}-split-${splitId}`
    : `unassigned-step-${orderId}-${itemId}-${step.sequence}`;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: draggableId,
    disabled: isSubcontracted || displayQty <= 0,
    data: {
      type: 'unassigned-step',
      orderId,
      orderNumber,
      itemId,
      itemIndex,
      productCode,
      productName,
      specification,
      plannedQuantity,        // Original item planned quantity (for item_planned_quantity field)
      displayQuantity: displayQty,  // Current display qty (for assigned_quantity field)
      stepSequence: step.sequence,
      stepName: step.name,
      standardTime: step.standard_time,
      completedQuantity,      // Original completed quantity
      isSubcontracted,
      isSplitChild,
      splitId,
      step,
    },
  });

  const style = transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined;

  const handleConfirmSplit = (splitQuantity) => {
    onSplit?.({
      orderId,
      orderNumber,
      itemId,
      itemIndex,
      productCode,
      productName,
      specification,
      stepSequence: step.sequence,
      stepName: step.name,
      standardTime: step.standard_time,
      plannedQuantity,
      completedQuantity,
      splitQuantity,
    });
    setSplitDialogOpen(false);
  };

  if (isSubcontracted) {
    return (
      <div
        ref={setNodeRef}
        className="p-2 mb-1.5 bg-gray-50 border border-gray-200 rounded-md opacity-60 cursor-not-allowed"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Factory className="h-3 w-3 text-gray-400" />
            <span className="text-xs font-medium text-gray-500">{step.name}</span>
          </div>
          <Badge variant="secondary" className="text-[10px] h-4 px-1">已外协</Badge>
        </div>
      </div>
    );
  }

  // If quantity is 0, show as disabled but still visible
  const isEmpty = displayQty <= 0;

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={`
          p-2 mb-1.5 border rounded-md text-xs
          transition-shadow hover:shadow-md
          ${isDragging || isOverlay ? 'shadow-lg ring-2 ring-blue-400 opacity-90' : ''}
          ${isSplitChild ? 'bg-cyan-50 border-cyan-200' : 'bg-white border-gray-200'}
          ${isSplitOriginal ? 'bg-amber-50 border-amber-200' : ''}
          ${isEmpty ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}
        `}
      >
        <div className="flex items-center gap-1">
          <GripVertical className="h-3 w-3 opacity-50 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1">
              <span className="font-semibold truncate" title={step.name}>
                {step.name}
              </span>
              {isSplitChild && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0 bg-cyan-100 text-cyan-700 border-cyan-300">
                  <GitBranch className="h-2.5 w-2.5 mr-0.5" />
                  拆分
                </Badge>
              )}
              {isSplitOriginal && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0 bg-amber-100 text-amber-700 border-amber-300">
                  原任务
                </Badge>
              )}
              {!isSplitChild && !isSplitOriginal && (
                <span className="text-[10px] text-gray-400 shrink-0">{step.standard_time}分</span>
              )}
            </div>
            <div className="text-[10px] text-gray-500 truncate">{productName || productCode}</div>
            {specification && (
              <div className="text-[10px] text-amber-600 truncate">{specification}</div>
            )}
            <div className="flex items-center justify-between">
              <div className="text-[10px] text-gray-500">
                数量: {displayQty}
                {isEmpty && <span className="text-red-400 ml-1">(已分完)</span>}
              </div>
              {displayQty > 1 && !isSplitChild && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSplitDialogOpen(true); }}
                  className="p-0.5 hover:bg-blue-50 rounded text-gray-400 hover:text-blue-500 transition-colors"
                  title="拆分数量"
                >
                  <Scissors className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <StepSplitDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        step={step}
        productName={productName}
        productCode={productCode}
        plannedQuantity={displayQty}
        onConfirm={handleConfirmSplit}
      />
    </>
  );
}

export default ProcessStepCard;
